import { getSessionIdentity, identityOfAccess, markSessionRefreshed, storeAccess } from '../session';
import { isNoResponse, RefreshUnavailableError, SessionChangedError } from './errors';
import { clearRefreshPending, isStaleRefresh, markRefreshPending, pause, wasRefreshInterrupted } from './refreshPending';
import { withSessionLock } from './sessionLock';
import { postWebSession, refreshTiming, sidOf, webSessionPath } from './webSession';

/** 進行中の更新。どのセッションのための更新かも持つ（別のセッションは待ち合わせない）。 */
let refreshInFlight: { session: string | null; promise: Promise<void> } | null = null;

/** 更新を送る（錠の中で呼ぶ）。sid はこのタブのセッションの sid。前の画面が途中で離れていたときだけ、401 の後に取り直す。 */
async function postRefresh(sid: string) {
  const interrupted = wasRefreshInterrupted();
  // 離れた画面の印があれば、その時刻を残す（取り直しの判断の起点）。無ければ、この更新の印を置く。
  if (!interrupted) markRefreshPending();
  for (let attempt = 0; ; attempt += 1) {
    try {
      const response = await postWebSession(webSessionPath(sid, 'refresh'), null, true);
      clearRefreshPending();
      return response;
    } catch (error) {
      const retry = interrupted && attempt < refreshTiming.interruptedRetries && isStaleRefresh(error);
      // 応答が届いたときは印を消す。ただし、失効した refresh の 401 で取り直すときは残す（前の画面の更新の応答が
      // まだ届いていない）。画面を離れるときは「切られた」（応答なし）として返るが、keepalive の要求は続いているので残す。
      if (!retry && !isNoResponse(error)) clearRefreshPending();
      if (!retry) throw error;
      await pause(refreshTiming.interruptedRetryDelayMs);
    }
  }
}

/**
 * Cookie の refresh を失効させる（錠の中で呼ぶ）。sid を添えると、サーバーは Cookie が
 * そのログインのものでなければ失効させず、Cookie も消さない（409 session_mismatch）。
 * 後始末のログアウトが、別のタブの新しいログインを壊さないため。
 * EV-491。経路もそのログインのもの（webSessionPath）。sid が分からなければ、どの Cookie も送れないので送らない。
 */
export async function revokeCookie(sid: string | null): Promise<boolean> {
  if (!sid) return false;
  try {
    await postWebSession(webSessionPath(sid, 'logout'), { sid });
    return true;
  } catch {
    return false;
  }
}

/**
 * Cookie の refresh で access を取り直す（錠の中で呼ぶ）。session はこのタブのセッション。
 * 返った access が、このタブのセッション（同じログイン・同じ利用者）のもので
 * なければ使わない（SessionChangedError）。
 */
export async function refreshWithinLock(session: string): Promise<void> {
  if (getSessionIdentity() !== session) throw new SessionChangedError();
  const sid = sidOf(session);
  // sid の無いセッションは作らない（identityOfAccess）。念のため、送れない経路は作らない。
  if (!sid) throw new SessionChangedError();
  let res;
  try {
    res = await postRefresh(sid);
  } catch (error) {
    if (getSessionIdentity() !== session) throw new SessionChangedError();
    // 応答が無い（timeout・通信断）は認証の失敗ではない。セッションは終えない。
    if (isNoResponse(error)) throw new RefreshUnavailableError(error);
    throw error;
  }
  const access = res?.data?.access;
  if (!access) throw new Error('Refresh response did not contain an access token');
  const current = getSessionIdentity();
  if (current === null) {
    // 応答を待つ間に別のタブでログアウトされた。回転した Cookie を受け取ってしまった
    // ので、ログアウト後に使える Cookie を残さないよう失効させる。錠を放す前に終える
    // （応答の Set-Cookie が、後から始まる新しいログインの Cookie を消さないように。
    // PR #90 レビュー 4176276497）。失敗は無視する。
    await revokeCookie(sidOf(session));
    throw new SessionChangedError();
  }
  // 応答を待つ間に別のタブでログインし直された、または Cookie が別のログインのもの。
  // 別のセッションのトークンを、このタブのセッションとして使わない。
  if (current !== session || identityOfAccess(access) !== session) {
    throw new SessionChangedError();
  }
  storeAccess(access);
  // 更新の世代を進める（endSession が、送った後に別のタブが更新したかを見分ける）。
  markSessionRefreshed();
}

/**
 * Cookie の refresh で access を取り直す。進行中の更新があれば待ち合わせる。
 *
 * refresh は使うたびに回転する（#87）。Cookie はタブ間で共有なので、複数のタブが
 * 同時に更新すると、サーバーは遅い側（回転前の Cookie）を拒否する。
 *  - Web Locks で、タブ間で更新を1つずつにする（後のタブは回転後の Cookie で送る）。
 *    Web Locks が無い接続では送らない（SessionEnvironmentError）。以前あった
 *    「拒否された後に別のタブの更新を待って取り直す」代替経路は、Web Locks の無い
 *    接続を対象外にしたので削除した。
 *  - 返った access が、このタブのセッション（同じログイン・同じ利用者）のもので
 *    なければ使わない（SessionChangedError）。Cookie は別のタブのログインで
 *    置き換わっていることがある。
 */
export function refreshAccessToken(expectedSession?: string | null): Promise<void> {
  const session = getSessionIdentity();
  if (expectedSession !== undefined && expectedSession !== session) {
    // 要求を送ったときのセッションではなくなっている。今のセッションを更新しない。
    return Promise.reject(new SessionChangedError());
  }
  if (refreshInFlight && refreshInFlight.session === session) return refreshInFlight.promise;
  if (!session) {
    return Promise.reject(new Error('No session to refresh'));
  }

  const promise = withSessionLock(() => refreshWithinLock(session)).finally(() => {
    if (refreshInFlight?.promise === promise) refreshInFlight = null;
  });
  refreshInFlight = { session, promise };

  return promise;
}
